import { useState, useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faNoteSticky,
  faPenToSquare,
  faCheck,
} from '@fortawesome/pro-regular-svg-icons';
import { useActionExecution } from '../../hooks/useActionExecution';
import { askMoo } from '../../../../queries/ask-moo';
import type { AssistantMessage, QuoteCopyNoteDraft } from '../../types';
import styles from './NoteDraftCard.module.css';
import useNotifications from '../../../../_lib/notifications/useNotifications';

interface Props {
  note: QuoteCopyNoteDraft;
  message: AssistantMessage;
}

/**
 * Quote Note auto-drafted after a copy quote is generated (OMNI-552).
 *
 * Rendered inline within the generated quote row. Unlike NoteDraftCard there is
 * no entity selector — the note is always attached to the just-generated quote
 * (`note.entityType`/`note.entityId`). The title/body are editable; Add Note
 * dispatches the existing `add-note` action with a stable `clientNoteId` so a
 * repeat click can't create a duplicate.
 */
export default function InlineQuoteNote({ note, message }: Props) {
  const notifications = useNotifications();
  const { mutateAsync, isLoading } = useActionExecution();
  const queryClient = useQueryClient();

  const [saved, setSaved] = useState(false);
  const [title, setTitle] = useState(note.title);
  const [noteText, setNoteText] = useState(note.noteText);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isDisabled = saved || isLoading;

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [noteText]);

  const handleSave = useCallback(async () => {
    try {
      const result = await mutateAsync({
        correlationId: message.correlationId,
        messageId: message.messageId,
        action: note.confirmAction || 'add-note',
        payload: {
          entityType: note.entityType,
          entityId: note.entityId,
          title,
          noteText,
          clientNoteId: note.clientNoteId,
        },
      });
      if (!result?.success) {
        notifications.error(
          result?.result?.message ?? 'Could not save note. Please try again.'
        );
        return;
      }
      setSaved(true);
      queryClient.invalidateQueries({
        queryKey: askMoo.conversation._def,
        refetchType: 'none',
      });
    } catch {
      notifications.error('Could not save note. Please try again.');
    }
  }, [mutateAsync, message, note, title, noteText, notifications, queryClient]);

  return (
    <div className={styles.noteCard}>
      <div className={styles.noteHeader}>
        <div className={styles.noteHeaderLeft}>
          <FontAwesomeIcon icon={faNoteSticky} style={{ fontSize: 14 }} />
          Quote Note
        </div>
        {saved && (
          <div className={styles.noteSavedBadge}>
            <FontAwesomeIcon icon={faCheck} style={{ fontSize: 11 }} />
            Note added
          </div>
        )}
      </div>

      <input
        className={styles.noteTitleInput}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        readOnly={isDisabled}
        disabled={isDisabled}
      />
      <textarea
        ref={textareaRef}
        className={styles.noteBodyTextarea}
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
        readOnly={isDisabled}
        disabled={isDisabled}
      />

      <div className={styles.noteActions}>
        <button
          className={
            isDisabled ? styles.noteSaveButtonDisabled : styles.noteSaveButton
          }
          onClick={handleSave}
          disabled={isDisabled}
          type="button"
        >
          <FontAwesomeIcon icon={faPenToSquare} style={{ fontSize: 12 }} />
          {isLoading ? 'Saving…' : 'Add Note'}
        </button>
      </div>
    </div>
  );
}
